/* The scoring terms a member plays under, and the fingerprint that says which
 * version of them they accepted.
 *
 * Text and version live in the same file on purpose. The fingerprint is what
 * decides whether a member gets re-prompted, so if the wording could change
 * without the version changing, someone would be recorded as having accepted
 * terms they were never shown.
 */

/* Bump when the WORDING below changes materially. A typo fix does not need it;
   a change to what a rule says does. Everyone re-acknowledges on a bump. */
export const RULES_TEXT_VERSION = 'v2'

/* Every line is rendered from the pool's real settings, never boilerplate. The
   tie rule especially is per-pool (`pools.tie_policy`), so a hardcoded "ties
   count as wins" would be actively false in a pool set to `no_credit` — worse
   than saying nothing, because members settle real money off-platform on these
   standings and would be wrong about their own record. */
const TIE_RULE: Record<string, string> = {
  all_correct:
    'A tied game counts as correct for everyone who picked it. Your record shows it as a win.',
  no_credit: 'A tied game counts for nobody. Everyone who picked it is marked incorrect.',
  exclude_game:
    'A tied game drops out of scoring entirely, so the week’s maximum score falls by one.',
}

export interface PoolRuleTerms {
  tiePolicy: string
  lockEnabled: boolean
  tiebreakerMethodId: string
}

/* Identifies the exact terms accepted: wording version plus the settings that
   wording described. A change to EITHER produces a value that no longer matches
   what is stored, which re-prompts — no extra column, no migration per tweak.

   The tiebreaker's display name and description are reference data keyed by
   tiebreaker_method_id, so the id alone pins them down. */
export function rulesFingerprint({ tiePolicy, lockEnabled, tiebreakerMethodId }: PoolRuleTerms) {
  return [
    RULES_TEXT_VERSION,
    tiePolicy,
    lockEnabled ? 'lock_on' : 'lock_off',
    tiebreakerMethodId,
  ].join(':')
}

export function buildPoolRules({
  tiePolicy,
  lockEnabled,
  tiebreakerName,
  tiebreakerDescription,
}: {
  tiePolicy: string
  lockEnabled: boolean
  tiebreakerName?: string
  tiebreakerDescription?: string
}) {
  const rules = [
    {
      label: 'Tied games',
      // Names the raw value rather than asserting a rule that may not be this
      // pool's, if a future tie_policy ships before this map does.
      body: TIE_RULE[tiePolicy] ?? `This pool uses the “${tiePolicy}” tie rule.`,
    },
    {
      label: 'Missing the deadline',
      body: 'Picks lock at the week’s first kickoff, whatever day that falls on. No picks in by then is a forfeit — zero for that week. You can change picks freely until the lock.',
    },
    {
      label: 'Lock of the week',
      body: lockEnabled
        ? 'Nominate one game as your Lock. Correct, it is worth 2 points instead of 1. Wrong, it is worth nothing — same as any miss.'
        : 'Turned off in this pool. Every correct pick is worth 1 point.',
    },
  ]

  if (tiebreakerName) {
    /* The method is real and frozen for the season, so it is named here. What
       is NOT real yet is the input: nothing in the app writes
       tiebreaker_predictions, so describing "predict the combined score" and
       stopping there tells members to do something the app gives them no way
       to do. The picks page shows the same prompt greyed out; this line is the
       written half of that, and the two have to say the same thing. */
    const notYet =
      ' Predictions open for the regular season — until then a tied week has co-winners rather than one.'
    rules.push({
      label: 'Tiebreaker',
      body:
        (tiebreakerDescription ? `${tiebreakerName} — ${tiebreakerDescription}` : tiebreakerName) +
        notYet,
    })
  }

  rules.push({
    label: 'Money',
    body: 'Side Action never handles money. Entry fees, pots and payouts are settled between members off-platform — the app only keeps the standings they are settled on.',
  })

  return rules
}
